// P14 — Modal renderizado quando user estoura cota diária/mensal de tokens
// (decisão Q4: hard block + botão pedir mais).
//
// Recebe o payload do 402 (`QuotaErrorResponse`); a partir dos `details`
// extrai used/limit/reset; tenta carregar a `MyQuota` pra abrir o "Pedir mais"
// no clique do botão.

import { FC, useEffect, useState } from 'react';
import quotaService from '@/services/quota.service';
import { MyQuota, QuotaErrorResponse, QuotaIncreaseField } from '@/types/agents';
import QuotaIncreaseRequestModal from './QuotaIncreaseRequestModal';

interface Props {
  error: QuotaErrorResponse;
  onClose: () => void;
}

function num(v: unknown): number {
  return typeof v === 'number' ? v : Number(v ?? 0);
}

function str(v: unknown): string | null {
  return typeof v === 'string' ? v : v == null ? null : String(v);
}

const QuotaBlockedModal: FC<Props> = ({ error, onClose }) => {
  const [quota, setQuota] = useState<MyQuota | null>(null);
  const [showRequest, setShowRequest] = useState(false);

  useEffect(() => {
    quotaService.getMyQuota().then(setQuota).catch(() => undefined);
  }, []);

  const used = num(error.details.used);
  const limit = num(error.details.limit);
  const resetsAt = str(error.details.resets_at);
  const initialField: QuotaIncreaseField =
    error.scope === 'tokens_monthly' ? 'max_tokens_per_month' : 'max_tokens_per_day';

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-[55] p-4"
      onClick={onClose}
      data-testid="quota-blocked-modal"
    >
      <div
        className="bg-surface-primary border border-surface-border rounded-xl max-w-md w-full p-6 space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-bold text-content-primary flex items-center gap-2">
          <span aria-hidden>⏸</span>
          Sua cota acabou
        </h2>
        <p className="text-sm text-content-primary">
          Você usou{' '}
          <strong>{used.toLocaleString('pt-BR')}</strong> de{' '}
          <strong>{limit.toLocaleString('pt-BR')}</strong> tokens{' '}
          {error.scope === 'tokens_monthly' ? 'este mês' : 'hoje'}.
        </p>
        {resetsAt && (
          <p className="text-sm text-content-tertiary">
            Reseta automaticamente em <strong>{resetsAt}</strong>.
          </p>
        )}
        <p className="text-xs text-content-tertiary">
          Você continua conseguindo abrir e ler conversas antigas.
        </p>

        <div className="flex gap-2 pt-3 border-t border-surface-border">
          <button
            type="button"
            onClick={onClose}
            data-testid="qbm-close"
            className="rounded-lg border border-surface-border px-4 py-2 text-sm text-content-primary"
          >
            Fechar
          </button>
          <button
            type="button"
            onClick={() => setShowRequest(true)}
            disabled={!quota}
            data-testid="qbm-request"
            className="flex-1 rounded-lg bg-accent text-surface-primary font-semibold px-4 py-2 text-sm disabled:opacity-50"
          >
            📨 Pedir mais ao admin
          </button>
        </div>
      </div>

      {showRequest && quota && (
        <QuotaIncreaseRequestModal
          quota={quota}
          initialField={initialField}
          onClose={() => setShowRequest(false)}
          onSubmitted={onClose}
        />
      )}
    </div>
  );
};

export default QuotaBlockedModal;
